import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { COMPARISONS, type Comparison } from "@/content/comparisons";
import { SITE } from "@/content/site";
import { buildSeo } from "@/lib/seo";
import { JsonLd } from "@/components/JsonLd";
import { Breadcrumbs } from "@/components/Breadcrumbs";

export const Route = createFileRoute("/compare/")({
  head: () =>
    buildSeo({
      title: "SEO Provider Comparisons — Freelancer Tamal",
      description:
        "Honest side-by-side comparisons: senior freelance SEO consultant vs. agencies, in-house hires, and marketplace gigs. Real pricing, real trade-offs, no spin.",
      path: "/compare",
      ogEyebrow: "Comparisons",
      ogTitle: "Compare SEO providers — agency vs. in-house vs. freelance",
    }),
  component: CompareHub,
});

function CompareHub() {
  return (
    <>
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Compare", path: "/compare" }]} />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "SEO provider comparisons",
          url: `${SITE.url}/compare`,
          isPartOf: { "@id": `${SITE.url}/#website` },
          hasPart: COMPARISONS.map((c) => ({
            "@type": "WebPage",
            name: c.title,
            url: `${SITE.url}/compare/${c.slug}`,
          })),
        }}
      />

      <header className="container-x pt-12 pb-10 lg:pt-20 lg:pb-14">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary mb-6">
          Compare
        </div>
        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.98]">
          Pick the <span className="text-gradient">right shape</span> for your SEO program.
        </h1>
        <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
          Side-by-side comparisons of a senior freelance SEO consultant vs. the
          common alternatives in the Bangladesh market — agencies, in-house
          hires, and marketplace gigs. Pricing, trade-offs, and best-fit
          scenarios, written without spin.
        </p>
      </header>

      <div className="container-x pb-32 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {COMPARISONS.map((c: Comparison) => (
          <Link
            key={c.slug}
            to="/compare/$slug"
            params={{ slug: c.slug }}
            className="group flex flex-col rounded-3xl border border-border p-8 hover:bg-card transition"
          >
            <div className="text-xs uppercase tracking-[0.18em] text-primary">
              Tamal vs. {c.themLabel}
            </div>
            <h2 className="mt-4 font-display text-2xl leading-snug">{c.title}</h2>
            <p className="mt-3 text-sm text-muted-foreground flex-1">{c.tagline}</p>
            <span className="mt-6 inline-flex items-center gap-1.5 text-sm text-primary">
              Read the comparison <ArrowUpRight size={14} />
            </span>
          </Link>
        ))}
      </div>
    </>
  );
}
